import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { api } from '../lib/api';
import { timecode } from '../lib/format';
import { useElementSize } from '../lib/hooks';
import type { Comment, Item, ItemVersion, Person, TimeAnchor } from '../lib/types';
import { Icon } from '../components/Icon';
import { Composer } from './Composer';
import { MediaPins } from './MediaPins';
import { composerPosition, shapeOf, type MediaPin, type Shape } from './geometry';
import type { CommentDraft } from './CommentInput';
import type { NewComment } from './useComments';

interface Props {
  item: Item;
  version: ItemVersion;
  projectId: string;
  people: Person[];
  comments: Comment[];
  selectedId: string | null;
  highlightId: string | null;
  mode: 'browse' | 'comment';
  showResolved: boolean;
  canComment: boolean;
  onSelect: (id: string | null) => void;
  onHover: (id: string | null) => void;
  onCreate: (input: NewComment) => Promise<Comment>;
}

const FRAME = 1 / 25; // frame rate is not exposed by browsers; one step = 40 ms
const PIN_WINDOW = 0.35; // seconds either side of a comment's timecode where its pin shows
const RATES = [0.5, 1, 1.5, 2];

/** Video review: every comment is tied to a timecode, optionally to a spot on that frame. */
export function VideoCanvas({ item, version, projectId, people, comments, selectedId, highlightId, mode, showResolved, canComment, onSelect, onHover, onCreate }: Props) {
  const asset = version.asset;
  const video = useRef<HTMLVideoElement>(null);
  const [stageRef, stage] = useElementSize<HTMLDivElement>();
  const [natural, setNatural] = useState({ width: asset.width || 0, height: asset.height || 0 });
  const [duration, setDuration] = useState(asset.duration || 0);
  const [time, setTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [rate, setRate] = useState(1);
  const [failed, setFailed] = useState(false);
  const [draft, setDraft] = useState<{ time: number; shape: Shape | null } | null>(null);
  const pendingSeek = useRef<number | null>(null);

  useEffect(() => {
    setDraft(null);
    setFailed(false);
    setTime(0);
    setPlaying(false);
  }, [asset.id]);

  useEffect(() => {
    if (mode === 'browse') setDraft(null);
  }, [mode]);

  // Smooth timecode while playing (timeupdate only fires about 4x a second).
  useEffect(() => {
    if (!playing) return;
    let raf = 0;
    const tick = () => {
      if (video.current) setTime(video.current.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);

  const seek = useCallback(
    (next: number) => {
      const element = video.current;
      if (!element) return;
      // Recordings straight from a browser can report an infinite duration; then there is no upper limit to hold to.
      const limit = Number.isFinite(element.duration) && element.duration > 0 ? element.duration : duration > 0 ? duration : Number.POSITIVE_INFINITY;
      const clamped = Math.max(0, Math.min(limit, next));
      if (!Number.isFinite(clamped)) return;
      element.currentTime = clamped;
      setTime(clamped);
    },
    [duration],
  );

  const toggle = useCallback(() => {
    const element = video.current;
    if (!element) return;
    if (element.paused) void element.play().catch(() => undefined);
    else element.pause();
  }, []);

  // Selecting a comment jumps to its moment. Also runs when the comment or the file arrives
  // after the selection (a link straight to a comment, or a comment on another version).
  const selectedComment = selectedId ? comments.find((c) => c.id === selectedId) : undefined;
  const selectedTime = selectedComment?.anchor.type === 'time' && (!selectedComment.versionId || selectedComment.versionId === version.id) ? selectedComment.anchor.time : null;
  useEffect(() => {
    if (selectedTime === null) return;
    const element = video.current;
    if (!element) return;
    element.pause();
    if (element.readyState >= 1) seek(selectedTime);
    else pendingSeek.current = selectedTime; // applied once the file's metadata is in
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seek changes identity with duration; that must not re-seek
  }, [selectedId, selectedTime, asset.id]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      // Not while typing, and not while a menu or dialog has the keyboard.
      if (target.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]') || document.querySelector('.dialog-scrim')) return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (event.key === ' ' && !target.closest('button, a')) {
        event.preventDefault();
        toggle();
      } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        video.current?.pause();
        const direction = event.key === 'ArrowRight' ? 1 : -1;
        seek((video.current?.currentTime ?? 0) + direction * (event.shiftKey ? 1 : FRAME));
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [seek, toggle]);

  const timed = useMemo(
    () => comments.filter((c) => c.anchor.type === 'time' && (!c.versionId || c.versionId === version.id) && (showResolved || c.status !== 'resolved' || c.id === selectedId)),
    [comments, version.id, showResolved, selectedId],
  );

  const pins = useMemo<MediaPin[]>(
    () =>
      timed
        .filter((c) => shapeOf(c) && (c.id === selectedId || Math.abs((c.anchor as TimeAnchor).time - time) <= PIN_WINDOW))
        .map((c) => ({ id: c.id, number: c.number, status: c.status, ...(shapeOf(c) as Shape) })),
    [timed, time, selectedId],
  );

  // Fit the frame inside the stage, leaving no letterboxing inside the pin layer.
  const padding = 32;
  const ratio = natural.width && natural.height ? natural.width / natural.height : 16 / 9;
  let width = Math.max(0, stage.width - padding);
  let height = width / ratio;
  if (height > stage.height - padding) {
    height = Math.max(0, stage.height - padding);
    width = height * ratio;
  }

  const onScrub = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!duration) return;
    const rect = event.currentTarget.getBoundingClientRect();
    seek(((event.clientX - rect.left) / rect.width) * duration);
  };

  const submit = async (input: CommentDraft) => {
    if (!draft) return;
    const comment = await onCreate({
      ...input,
      itemId: item.id,
      versionId: version.id,
      anchor: { type: 'time', time: draft.time, duration: duration || null, ...(draft.shape || {}), mediaWidth: natural.width || null, mediaHeight: natural.height || null },
    });
    setDraft(null);
    onSelect(comment.id);
  };

  const composerStyle = draft
    ? draft.shape
      ? composerPosition((stage.width - width) / 2 + (draft.shape.x + (draft.shape.width ?? 0)) * width, (stage.height - height) / 2 + draft.shape.y * height, stage)
      : { left: 16, bottom: 16 }
    : null;

  return (
    <div className="canvas">
      <div ref={stageRef} className={`canvas-stage ${mode === 'comment' ? 'is-commenting' : ''}`}>
        {failed ? (
          <div className="center-fill" role="alert" style={{ height: '100%' }}>
            <Icon name="alert" className="icon-lg" />
            <h3>This video could not be played</h3>
            <p className="muted">Your browser may not support this format. MP4 (H.264) and WebM play everywhere. You can still download the file.</p>
            <a className="btn btn-secondary" href={asset.downloadUrl}>
              <Icon name="download" /> Download
            </a>
          </div>
        ) : (
          <div className="media-scroll" style={{ padding: 0, alignItems: 'center', justifyContent: 'center' }}>
            <div className={`media-frame ${mode === 'comment' && canComment ? 'is-commenting' : ''}`} style={{ width, height }}>
              <video
                ref={video}
                src={asset.url}
                playsInline
                preload="metadata"
                muted={muted}
                onLoadedMetadata={(event) => {
                  const element = event.currentTarget;
                  const length = Number.isFinite(element.duration) ? element.duration : 0;
                  setDuration(length);
                  setNatural({ width: element.videoWidth, height: element.videoHeight });
                  element.playbackRate = rate; // a new file resets the speed; keep the one on the button
                  if (pendingSeek.current !== null) {
                    const target = Math.max(0, length ? Math.min(length, pendingSeek.current) : pendingSeek.current);
                    pendingSeek.current = null;
                    element.currentTime = target;
                    setTime(target);
                  }
                  // Save what only the browser can measure, once.
                  if (length && (!asset.duration || !asset.width)) {
                    void api.patch(`/api/assets/${asset.id}`, { duration: length, width: element.videoWidth, height: element.videoHeight }).catch(() => undefined);
                  }
                }}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onTimeUpdate={(event) => setTime(event.currentTarget.currentTime)}
                onError={() => setFailed(true)}
              />
              <MediaPins
                pins={pins}
                draft={draft?.shape ?? null}
                selectedId={selectedId}
                highlightId={highlightId}
                commenting={mode === 'comment' && canComment}
                onPlace={(shape) => {
                  video.current?.pause();
                  onSelect(null);
                  setDraft({ time: video.current?.currentTime ?? time, shape });
                }}
                onSelect={onSelect}
                onHover={onHover}
                onBackgroundClick={toggle}
              />
            </div>
          </div>
        )}
        {draft && composerStyle && (
          <Composer title={`Revision at ${timecode(draft.time)}`} style={composerStyle} focusKey={`${draft.time}|${draft.shape?.x}|${draft.shape?.y}`} projectId={projectId} people={people} onSubmit={submit} onCancel={() => setDraft(null)} />
        )}
      </div>

      <div className="video-controls">
        <div className="timeline">
          <div className="timeline-bar" onPointerDown={(event) => (event.currentTarget.setPointerCapture(event.pointerId), onScrub(event))} onPointerMove={(event) => event.buttons === 1 && onScrub(event)} role="slider" aria-label="Seek" aria-valuemin={0} aria-valuemax={Math.round(duration)} aria-valuenow={Math.round(time)} aria-valuetext={timecode(time)} tabIndex={0}>
            <div className="timeline-track" />
            <div className="timeline-fill" style={{ width: duration ? `${(time / duration) * 100}%` : 0 }} />
            <div className="timeline-head" style={{ left: duration ? `${(time / duration) * 100}%` : 0 }} />
          </div>
          {duration > 0 &&
            timed.map((comment) => (
              <button
                key={comment.id}
                type="button"
                className={`timeline-marker s-${comment.status} ${comment.id === selectedId ? 'is-active' : ''}`}
                style={{ left: `${Math.min(100, ((comment.anchor as TimeAnchor).time / duration) * 100)}%` }}
                aria-label={`Revision ${comment.number} at ${timecode((comment.anchor as TimeAnchor).time)}`}
                onClick={() => onSelect(comment.id)}
                onMouseEnter={() => onHover(comment.id)}
                onMouseLeave={() => onHover(null)}
              >
                {comment.number}
              </button>
            ))}
        </div>

        <div className="video-row">
          <div className="row" style={{ gap: 4 }}>
            <button type="button" className="icon-btn" aria-label="Back one frame" title="Back one frame (Left arrow)" onClick={() => (video.current?.pause(), seek(time - FRAME))}>
              <Icon name="step-back" />
            </button>
            <button type="button" className="icon-btn is-active" aria-label={playing ? 'Pause' : 'Play'} title="Play or pause (Space)" onClick={toggle}>
              <Icon name={playing ? 'pause' : 'play'} />
            </button>
            <button type="button" className="icon-btn" aria-label="Forward one frame" title="Forward one frame (Right arrow)" onClick={() => (video.current?.pause(), seek(time + FRAME))}>
              <Icon name="step-forward" />
            </button>
            <span className="timecode" style={{ marginLeft: 8 }}>
              {timecode(time)} <span className="muted">/ {timecode(duration)}</span>
            </span>
          </div>
          <div className="row" style={{ gap: 4 }}>
            {canComment && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  video.current?.pause();
                  onSelect(null);
                  setDraft({ time: video.current?.currentTime ?? time, shape: null });
                }}
              >
                <Icon name="message" /> Revision at {timecode(time, false)}
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost btn-sm mono"
              aria-label={`Playback speed ${rate}x`}
              title="Playback speed"
              onClick={() => {
                const next = RATES[(RATES.indexOf(rate) + 1) % RATES.length];
                setRate(next);
                if (video.current) video.current.playbackRate = next;
              }}
            >
              {rate}x
            </button>
            <button type="button" className="icon-btn" aria-label={muted ? 'Unmute' : 'Mute'} aria-pressed={muted} onClick={() => setMuted((value) => !value)}>
              <Icon name={muted ? 'mute' : 'volume'} />
            </button>
            <a className="icon-btn" href={asset.downloadUrl} aria-label="Download this file" title="Download this file">
              <Icon name="download" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
